'use-client';
import * as React from 'react';
import classnames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
  getTabIndexAttribute,
} from '@wix/editor-elements-common-utils';
import { useVideoAPI } from '@wix/editor-elements-common-utils/hooks';
import type { MediaContainerVideoAPI } from '@wix/editor-elements-types/components';
import type { ISectionProps } from '../Section.types';
import { TestIds } from '../constants';
import BackgroundX from '../../../BackgroundX/viewer/BackgroundX';
import ShapeDividers from '../../../MediaContainers/ShapeDividers/viewer/shapeDividers';
import sectionSemanticClassNames from '../Section.semanticClassNames';
import style from './styles/Section.scss';
import { PauseSmall, PlaySmall } from '@wix/wix-ui-icons-common';

const Section: React.ForwardRefRenderFunction<
  MediaContainerVideoAPI,
  ISectionProps
> = (props, compRef) => {
  const {
    id,
    skin = 'RectangleArea',
    className,
    containerRootClassName = '',
    customClassNames = [],
    containerProps,
    children,
    fillLayers = props.fillLayers || props.background,
    tagName,
    getPlaceholder,
    dividers,
    semanticClassNames,
    onStop,
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    lang,
    translations,
    isPlayPauseSectionExperimentOn,
    isBuilder,
  } = props;
  const TagName = tagName || ('section' as keyof JSX.IntrinsicElements);
  const { shouldOmitWrapperLayers } = containerProps;
  const classNames = classnames(
    style[skin],
    containerRootClassName,
    className,
    semanticClassNames
      ? formatClassNames(semanticClassNames.root, ...customClassNames)
      : formatClassNames(sectionSemanticClassNames.root, ...customClassNames),
    {
      [style.shouldOmitWrapperLayers]: shouldOmitWrapperLayers,
    },
  );
  const hasVideo = !!fillLayers?.video;
  const videoRef = useVideoAPI(compRef, hasVideo, onStop);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(
    !videoRef?.current?.paused || false,
  );
  const handlePlayPause = () => {
    const video = videoRef?.current;
    if (video) {
      video.paused ? video.play() : video.pause();
      setIsVideoPlaying(prev => {
        const newPlayingState = !prev;
        // Find and control all child video elements
        const sectionElement = document.getElementById(id);
        if (sectionElement) {
          const childVideos = sectionElement.querySelectorAll('video');
          childVideos.forEach(childVideo => {
            if (childVideo !== video) {
              // Use the new playing state to sync all videos
              newPlayingState ? childVideo.play() : childVideo.pause();
            }
          });
        }
        return newPlayingState;
      });
    }
  };

  return (
    <TagName
      id={id}
      {...getDataAttributes(props)}
      {...getTabIndexAttribute(props.a11y || { tabIndex: -1 })}
      data-block-level-container="Section"
      className={`${classNames} ${style.section}`}
      data-testid={TestIds.section}
      onClick={onClick}
      onDoubleClick={onDblClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      lang={lang}
    >
      {fillLayers && !isBuilder && (
        <BackgroundX
          {...fillLayers}
          videoRef={videoRef}
          getPlaceholder={getPlaceholder}
        />
      )}
      {hasVideo && isPlayPauseSectionExperimentOn && !isBuilder && (
        <button
          className={`${style.videoPlayPauseButton}`}
          onClick={handlePlayPause}
          aria-pressed={isVideoPlaying}
          aria-label={translations?.ariaLabel}
        >
          {isVideoPlaying ? <PauseSmall /> : <PlaySmall />}
        </button>
      )}
      {dividers && !isBuilder && <ShapeDividers {...dividers} />}
      {children()}
    </TagName>
  );
};

export default React.forwardRef(Section);
